'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import HouseAd from './HouseAd';
import { useAdFallback } from './useAdFallback';

const ADSENSE_CLIENT_ID = 'ca-pub-2214184889025947';

/**
 * Native in-article (fluid) ad — designed to sit between sections of an article.
 * Used for the single mid-article placement (see splitAtMidHeading). Blends with
 * body content, so it reads as part of the page rather than a boxed banner.
 * Requires the adsbygoogle.js loader (<AdSenseLoader />) on the page.
 */
export default function InArticleAd({ slot }: { slot: string }) {
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement | null>(null);
  const pathname = usePathname();
  const { unfilled, blocked } = useAdFallback(insRef, { slot: slot, placement: 'in-article' });

  useEffect(() => {
    if (pushed.current) return;
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      // Loader not ready yet or slot already filled — safe to ignore.
    }
  }, []);

  return (
    <div className="not-prose my-6">
      {!unfilled && (
        <div className="mb-1 select-none text-center text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Advertisement
        </div>
      )}
        {/* Unmount the <ins> once it is known unfilled rather than just hiding
            it. Ad blockers apply cosmetic filters as well as network blocks,
            and a :has(> ins.adsbygoogle) rule would hide this whole container —
            taking the house ad with it. Removing the element means such a rule
            has nothing to match. Detection has already completed by this point
            (useAdFallback settles once), so the ref is no longer needed. */}
      {!unfilled && (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', textAlign: 'center' }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client={ADSENSE_CLIENT_ID}
          data-ad-slot={slot}
        />
      )}
      {unfilled && <HouseAd pathname={pathname} blocked={blocked} />}
    </div>
  );
}
